import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import ListItemIcon from '@mui/material/ListItemIcon';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import LogoutIcon from '@mui/icons-material/Logout';

import {
  OptionsDropdownItem,
  OptionsDropdownDivider,
} from 'components/shared/OptionsDropdown';
import { AccountMode } from 'reducers/settings.reducer';
import { changeToMode } from 'redux-actions/settings.actions';

const UserMenuContainer = styled('div')(() => {
  return {
    width: 250,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };
});

const MenuTop = styled('div')({
  flex: '1 1 auto',
});

const MenuBottom = styled('div')({
  flexShrink: 0,
  marginTop: 'auto',
});

const MenuIcon = styled(ListItemIcon)(() => {
  return {
    minWidth: 40,
  };
});

const StyledAvatarButton = styled(IconButton)(({ theme }) => {
  return {
    ...theme.mixins.toolbar,
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
  };
});

const DEFAULT_ACL: any = {};

const UserMenu = ({ currentUser, acl = DEFAULT_ACL }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useSelector((state) => {
    return state.settings;
  }) as any;

  const toggleUserMenu = useCallback((open) => {
    return () => {
      setMenuOpen(open);
    };
  }, []);

  const handleChangeToMode = useCallback(
    (mode) => {
      dispatch(changeToMode(mode));
    },
    [dispatch]
  );

  const renderStrategistOptions = useCallback(() => {
    const isStrategist = acl.strategist || false;
    const isProfessionalModeActive =
      settings.mode === AccountMode.Professional || false;

    if (!isStrategist) {
      return null;
    }

    const { mode, path, title, Icon } = (() => {
      let innerMode: any = AccountMode.Professional;
      let innerPath = '/accounts';
      let innerTitle = 'View Professional';
      let InnerIcon = WorkOutlineIcon;
      if (isProfessionalModeActive) {
        innerMode = AccountMode.Personal;
        innerPath = '/portfolios';
        innerTitle = 'View Personal';
        InnerIcon = HomeOutlinedIcon;
      }
      return {
        mode: innerMode,
        path: innerPath,
        title: innerTitle,
        Icon: InnerIcon,
      };
    })();

    return [
      <OptionsDropdownDivider key="switch-mode-divider" />,
      <OptionsDropdownItem
        key="switch-mode-option"
        onClick={() => {
          handleChangeToMode(mode);
          navigate(path);
        }}
      >
        <MenuIcon>
          <Icon fontSize="small" />
        </MenuIcon>
        {title}
      </OptionsDropdownItem>,
    ];
  }, [acl.strategist, settings.mode, navigate, handleChangeToMode]);

  const avatar = currentUser.photoURL;
  const isAdmin = acl.admin || false;
  const buttonChildren = avatar ? (
    <Avatar alt="Avatar" src={avatar} />
  ) : (
    <AccountCircleIcon />
  );

  return (
    <>
      <StyledAvatarButton
        onClick={toggleUserMenu(true)}
        color="inherit"
        size="large"
      >
        {buttonChildren}
      </StyledAvatarButton>
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={toggleUserMenu(false)}
        onClick={toggleUserMenu(false)}
      >
        <UserMenuContainer>
          <MenuTop>
            <OptionsDropdownItem
              onClick={() => {
                navigate('/profile');
              }}
            >
              <MenuIcon>
                <PersonOutlineIcon fontSize="small" />
              </MenuIcon>
              Profile
            </OptionsDropdownItem>
            {renderStrategistOptions()}
            {isAdmin && [
              <OptionsDropdownDivider key="admin-divider" />,
              <OptionsDropdownItem
                key="admin-users-option"
                onClick={() => {
                  navigate('/admin/users');
                }}
              >
                <MenuIcon>
                  <GroupOutlinedIcon fontSize="small" />
                </MenuIcon>
                Users
              </OptionsDropdownItem>,
              <OptionsDropdownDivider key="admin-style-guide-divider" />,
              <OptionsDropdownItem
                key="admin-style-guide-option"
                onClick={() => {
                  navigate('/admin/style-guide');
                }}
              >
                <MenuIcon>
                  <PaletteOutlinedIcon fontSize="small" />
                </MenuIcon>
                Style Guide
              </OptionsDropdownItem>,
            ]}
          </MenuTop>
          <MenuBottom>
            <OptionsDropdownDivider />
            <OptionsDropdownItem
              onClick={() => {
                navigate('/auth/logout');
              }}
            >
              <MenuIcon>
                <LogoutIcon fontSize="small" />
              </MenuIcon>
              Logout
            </OptionsDropdownItem>
          </MenuBottom>
        </UserMenuContainer>
      </Drawer>
    </>
  );
};

UserMenu.propTypes = {
  currentUser: PropTypes.object.isRequired,
  acl: PropTypes.object,
};

export default UserMenu;
